import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, Award, DollarSign, Calendar, Clock, Building } from 'lucide-react';

const ContractAwardsManager = () => {
  const api = useApi();
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAward, setEditingAward] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state for creating/editing contract awards
  const [formData, setFormData] = useState({
    client: '',
    project: '',
    value: '',
    year: new Date().getFullYear().toString(),
    duration: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    loadAwards();
  }, []);

  const loadAwards = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/contract-awards');
      if (response.success) {
        setAwards(response.data);
      }
    } catch (error) {
      console.error('Error loading contract awards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAward = async () => {
    try {
      const awardData = {
        ...formData,
        order: awards.length + 1
      };

      const response = await api.post('/admin/contract-awards', awardData);
      if (response.success) {
        loadAwards();
        resetForm();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating contract award:', error);
    }
  };

  const handleUpdateAward = async () => {
    try {
      const response = await api.put(`/admin/contract-awards/${editingAward}`, formData);
      if (response.success) {
        loadAwards();
        resetForm();
        setEditingAward(null);
      }
    } catch (error) {
      console.error('Error updating contract award:', error);
    }
  };

  const handleDeleteAward = async (awardId) => {
    if (window.confirm('Are you sure you want to delete this contract award?')) {
      try {
        const response = await api.delete(`/admin/contract-awards/${awardId}`);
        if (response.success) {
          loadAwards();
        }
      } catch (error) {
        console.error('Error deleting contract award:', error);
      }
    }
  };

  const handleToggleActive = async (awardId) => {
    try {
      const response = await api.put(`/admin/contract-awards/${awardId}/toggle`);
      if (response.success) {
        loadAwards();
      }
    } catch (error) {
      console.error('Error toggling award status:', error);
    }
  };

  const startEdit = (award) => {
    setFormData({
      client: award.client,
      project: award.project,
      value: award.value,
      year: award.year,
      duration: award.duration,
      order: award.order,
      is_active: award.is_active
    });
    setEditingAward(award.id);
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      client: '',
      project: '',
      value: '',
      year: new Date().getFullYear().toString(),
      duration: '',
      order: 0,
      is_active: true
    });
  };

  const formatValue = (value) => {
    if (value.includes('$')) return value;
    const num = parseFloat(value.replace(/[^\d.]/g, ''));
    if (isNaN(num)) return value;
    
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(0)}K`;
    } else {
      return `$${num.toLocaleString()}`;
    }
  };

  const AwardCard = ({ award }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <Award className="w-6 h-6 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{award.project}</h3>
            <div className="flex items-center space-x-1 text-gray-600 mb-2">
              <Building size={14} />
              <span className="text-sm font-medium">{award.client}</span>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <DollarSign size={14} />
                <span className="font-medium text-green-600">{formatValue(award.value)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar size={14} />
                <span>{award.year}</span>
              </div>
              {award.duration && (
                <div className="flex items-center space-x-1">
                  <Clock size={14} />
                  <span>{award.duration}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleActive(award.id)}
            className={`p-2 rounded-lg ${award.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
            title={award.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
          >
            {award.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
          <button
            onClick={() => startEdit(award)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="Edit contract award"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteAward(award.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            title="Delete contract award"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">Order: {award.order}</span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          award.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {award.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
    </div>
  );

  const AwardForm = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingAward ? 'Edit Contract Award' : 'Create New Contract Award'}
        </h3>
        <button
          onClick={() => {
            setEditingAward(null);
            setShowCreateForm(false);
            resetForm();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
        >
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client/Organization</label>
            <div className="relative">
              <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.client}
                onChange={(e) => setFormData(prev => ({...prev, client: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Government Agency / Organization"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
            <input
              type="text"
              value={formData.project}
              onChange={(e) => setFormData(prev => ({...prev, project: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Digital Transformation Initiative"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contract Value</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({...prev, value: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1.2M or $1,200,000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.year}
                onChange={(e) => setFormData(prev => ({...prev, year: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="2024"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
            <div className="relative">
              <Clock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({...prev, duration: e.target.value}))}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="18 months"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Active (visible to public)
          </label>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-200">
          <h4 className="text-sm font-medium text-gray-600 mb-3">Preview</h4>
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  {formData.project || 'Project Name'}
                </h3>
                <div className="flex items-center space-x-1 text-gray-600 mt-1">
                  <Building size={12} />
                  <span className="text-sm">{formData.client || 'Client Name'}</span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                  <div className="flex items-center space-x-1">
                    <DollarSign size={12} />
                    <span className="font-medium text-green-600">
                      {formData.value ? formatValue(formData.value) : '$0'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar size={12} />
                    <span>{formData.year || '2024'}</span>
                  </div>
                  {formData.duration && (
                    <div className="flex items-center space-x-1">
                      <Clock size={12} />
                      <span>{formData.duration}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={editingAward ? handleUpdateAward : handleCreateAward}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            <span>{editingAward ? 'Update Award' : 'Create Award'}</span>
          </button>
          <button
            onClick={() => {
              setEditingAward(null);
              setShowCreateForm(false);
              resetForm();
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contract Awards Manager</h2>
          <p className="text-gray-600 mt-1">Showcase your government and enterprise contract wins</p>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingAward(null);
            resetForm();
          }}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          <span>Add Award</span>
        </button>
      </div>

      {/* Summary Stats */}
      {awards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Award className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">
                {awards.filter(a => a.is_active).length} Active Awards
              </span>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">
                Latest: {Math.max(...awards.map(a => parseInt(a.year) || 0))}
              </span>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Building className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-800">
                {new Set(awards.map(a => a.client)).size} Unique Clients
              </span>
            </div>
          </div>
        </div>
      )}

      {(showCreateForm || editingAward) && <AwardForm />}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {awards.map(award => (
          <AwardCard key={award.id} award={award} />
        ))}
      </div>

      {awards.length === 0 && !showCreateForm && (
        <div className="text-center py-12">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No contract awards found</p>
          <button
            onClick={() => {
              setShowCreateForm(true);
              resetForm();
            }}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Create your first contract award
          </button>
        </div>
      )}
    </div>
  );
};

export default ContractAwardsManager;